// Agent → /api/chat request settings, shared by the browser (chat-view,
// use-agents) and the server (external agent chat route), so both build the
// same request from the same agent.
import {
  BUILTIN_TOOL_UUID,
  BUILTIN_UUID_TO_TOOL_ID,
  KB_BUILTIN_TOOL_UUID,
  RESOLVE_DATE_TIME_BUILTIN_TOOL_UUID,
  WEB_SEARCH_BUILTIN_TOOL_UUID,
} from "@/lib/builtin-tools";
import { isValidModelId } from "@/lib/model-id";
import type { DbAgent, DbTool } from "@/types/db";
import {
  getAgentSettings,
  resolveTools,
  TOOL_IDS,
  type ActiveToolConfig,
  type UserSettings,
} from "@/types/settings";

export type AgentToolRow = {
  config: Record<string, unknown> | null;
  tools:
    | { id: string; source_type: string }
    | { id: string; source_type: string }[]
    | null;
};

export function normaliseAgentToolRows(
  rows: AgentToolRow[] | null
): {
  tools: string[];
  knowledge_base_config: DbAgent["knowledge_base_config"];
  web_search_config: DbAgent["web_search_config"];
  resolve_date_time_config: DbAgent["resolve_date_time_config"];
} {
  // Builtin ToolIds and/or custom tool DB UUIDs — chat-view.tsx resolves each
  // kind differently (builtins via BUILTIN_TOOL_UUID, custom tools by raw id).
  const toolIds: string[] = [];
  let kbConfig: DbAgent["knowledge_base_config"] = null;
  let wsConfig: DbAgent["web_search_config"] = null;
  let resolveDateTimeConfig: DbAgent["resolve_date_time_config"] = null;

  for (const at of rows ?? []) {
    const tool = Array.isArray(at.tools) ? (at.tools[0] ?? null) : at.tools;
    if (!tool) continue;
    if (tool.source_type === "custom") {
      toolIds.push(tool.id);
      continue;
    }
    const toolId = BUILTIN_UUID_TO_TOOL_ID[tool.id];
    if (toolId) {
      toolIds.push(toolId);
      if (tool.id === KB_BUILTIN_TOOL_UUID) {
        const cfg = at.config ?? {};
        kbConfig = {
          fileIds: Array.isArray(cfg.fileIds) ? (cfg.fileIds as string[]) : [],
          collectionIds: Array.isArray(cfg.collectionIds) ? (cfg.collectionIds as string[]) : [],
        };
      }
      if (tool.id === WEB_SEARCH_BUILTIN_TOOL_UUID) {
        const cfg = at.config ?? {};
        wsConfig = {
          allowed_domains: Array.isArray(cfg.allowed_domains) ? (cfg.allowed_domains as string[]) : [],
          blocked_domains: Array.isArray(cfg.blocked_domains) ? (cfg.blocked_domains as string[]) : [],
        };
      }
      if (tool.id === RESOLVE_DATE_TIME_BUILTIN_TOOL_UUID) {
        const cfg = at.config ?? {};
        const defaultTimezone =
          typeof cfg.default_timezone === "string" && cfg.default_timezone.trim().length > 0
            ? cfg.default_timezone
            : "Pacific/Auckland";
        resolveDateTimeConfig = {
          default_timezone: defaultTimezone,
        };
      }
    }
  }

  return {
    tools: toolIds,
    knowledge_base_config: kbConfig,
    web_search_config: wsConfig,
    resolve_date_time_config: resolveDateTimeConfig,
  };
}

/**
 * An agents row joined with agent_tools → DbAgent (without teams, which the
 * chat request doesn't need). When tools_configured is true the tool list is
 * authoritative (may be empty); otherwise null, so the user's profile tools
 * apply.
 */
export function agentFromRow(
  row: { tools_configured?: boolean; agent_tools?: AgentToolRow[] | null } & Record<string, unknown>
): DbAgent {
  const { agent_tools, ...agent } = row;
  const { tools, knowledge_base_config, web_search_config, resolve_date_time_config } =
    normaliseAgentToolRows(agent_tools ?? null);
  return {
    ...agent,
    teams: [],
    tools: agent.tools_configured ? tools : null,
    knowledge_base_config,
    web_search_config,
    resolve_date_time_config,
  } as unknown as DbAgent;
}

export type AgentRuntimeContext = {
  includeProfileContext: boolean;
  includeTeamInstructions: boolean;
  includeWoodsContext: boolean;
  includeNzContext: boolean;
  includeCivilEngineeringContext: boolean;
};

export type AgentRequestOverride = {
  model: string;
  agentId: string;
  agentPrompt: string | null;
  agentContext: AgentRuntimeContext;
  activeTools: ActiveToolConfig[];
  source: string;
};

// Builds the request body override for an agent turn. Tools are computed from
// the agent's own settings only — this deliberately bypasses any per-chat tool
// overrides (chatTools), which were curated for a chat, not for the agent.
export function buildAgentRequestOverride(
  agent: DbAgent,
  settings: UserSettings,
  dbTools: DbTool[]
): AgentRequestOverride {
  const agentSettings = getAgentSettings(agent);
  const effectiveTools = resolveTools(settings, agentSettings);
  const kbConfig = agent.knowledge_base_config ?? null;
  const wsConfig = agent.web_search_config ?? null;
  const rdtConfig = agent.resolve_date_time_config ?? {
    default_timezone: settings.resolveDateTimeConfig.defaultTimezone,
  };

  const builtinConfigs = TOOL_IDS
    .filter((toolId) => effectiveTools[toolId])
    .flatMap((toolId): ActiveToolConfig[] => {
      const uuid = BUILTIN_TOOL_UUID[toolId];
      const dbTool = dbTools.find((t) => t.id === uuid);
      if (!dbTool) return [];
      return [{
        toolId,
        executorConfig: dbTool.executor_config as ActiveToolConfig["executorConfig"],
        ...(toolId === "knowledgeBase" && kbConfig ? { config: kbConfig as Record<string, unknown> } : {}),
        ...(toolId === "webSearch" && wsConfig ? { config: wsConfig as Record<string, unknown> } : {}),
        ...(toolId === "resolveDateTime" ? { config: rdtConfig as Record<string, unknown> } : {}),
      }];
    });

  const assignedCustomToolIds = agent.tools ?? null;
  const customToolConfigs: ActiveToolConfig[] = assignedCustomToolIds
    ? dbTools
        .filter((t) => t.source_type === "custom" && assignedCustomToolIds.includes(t.id))
        .map((t) => ({
          toolId: t.id,
          executorConfig: t.executor_config as ActiveToolConfig["executorConfig"],
          executorType: t.executor_type,
          schema: t.schema as ActiveToolConfig["schema"],
          name: t.name,
          description: t.description,
          config: {},
        }))
    : [];

  return {
    model: isValidModelId(agent.model) ? agent.model : settings.model,
    agentId: agent.id,
    agentPrompt: agent.prompt?.trim() || null,
    agentContext: {
      includeProfileContext: agent.include_profile_context,
      includeTeamInstructions: agent.include_team_instructions,
      includeWoodsContext: agent.include_woods_context,
      includeNzContext: agent.include_nz_context,
      includeCivilEngineeringContext: agent.include_civil_engineering_context,
    },
    // Mirrors useChatTransport's isAgentChat handling: agent turns exclude
    // "memory", and "agent-chat" keeps agent-scoped tools (e.g. knowledgeBase)
    // from being stripped server-side by the general-chat scoping rule.
    activeTools: [...builtinConfigs, ...customToolConfigs].filter((t) => t.toolId !== "memory"),
    source: "agent-chat",
  };
}
